import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Bell, Smartphone, Trash2, Send, Power, Pencil, Share, PlusSquare, ShieldCheck, Activity } from "lucide-react";
import { api } from "@/lib/api";
import { pushSupported, isStandalone, isIOS, enablePush } from "@/lib/push";

const CATEGORY_LABELS = {
  leads: "New leads & quote requests",
  quotes: "Quote decisions (accepted / declined)",
  rescue: "Ascendia Rescue (urgent)",
  bookings: "Booking / date requests",
  messages: "Customer questions & messages",
  uploads: "Photo / video uploads",
  quote_activity: "Quote activity (viewed, revisions)",
};

export const AdminNotifications = ({ isOwner }) => {
  const [devices, setDevices] = useState([]);
  const [prefs, setPrefs] = useState(null);
  const [log, setLog] = useState([]);
  const [busy, setBusy] = useState(false);
  const supported = pushSupported();
  const iosNeedsInstall = isIOS() && !isStandalone();

  const loadDevices = () => api.get("/admin/push/devices").then(({ data }) => setDevices(data)).catch(() => {});
  const loadPrefs = () => api.get("/admin/notifications/prefs").then(({ data }) => setPrefs(data)).catch(() => {});
  const loadLog = () => { if (isOwner) api.get("/admin/push/delivery-log").then(({ data }) => setLog(data)).catch(() => {}); };

  useEffect(() => { loadDevices(); loadPrefs(); loadLog(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const enable = async () => {
    setBusy(true);
    try {
      await enablePush();
      toast.success("This device is now registered for notifications");
      loadDevices();
    } catch (e) {
      const msg = e.message === "denied" ? "Notification permission was blocked in your browser settings"
        : e.message === "unsupported" ? "This browser/device doesn't support web push"
        : "Could not enable notifications. Please try again.";
      toast.error(msg);
    } finally { setBusy(false); }
  };

  const rename = async (d) => {
    const label = window.prompt("Rename this device", d.label);
    if (!label) return;
    await api.patch(`/admin/push/devices/${d.id}`, { label }).catch(() => toast.error("Rename failed"));
    loadDevices();
  };
  const toggle = async (d) => { await api.patch(`/admin/push/devices/${d.id}`, { enabled: !d.enabled }).catch(() => {}); loadDevices(); };
  const remove = async (d) => { if (!window.confirm("Remove this device?")) return; await api.delete(`/admin/push/devices/${d.id}`).catch(() => {}); loadDevices(); };
  const test = async () => {
    try { const { data } = await api.post("/admin/push/test"); const r = data.result || {}; toast.success(`Test sent to ${r.targeted || 0} device(s)`); loadLog(); }
    catch { toast.error("Test failed"); }
  };

  const setMode = async (mode) => { const { data } = await api.put("/admin/notifications/prefs", { ...prefs, mode }); setPrefs(data); };
  const toggleCat = async (c) => { const next = { ...prefs, categories: { ...prefs.categories, [c]: !prefs.categories[c] } }; const { data } = await api.put("/admin/notifications/prefs", next); setPrefs(data); };

  return (
    <div className="max-w-3xl space-y-6" data-testid="admin-notifications">
      {/* Install screen (iOS) */}
      {iosNeedsInstall && (
        <div className="ascendia-card p-6" data-testid="pwa-install-card">
          <div className="flex items-center gap-2 text-gold-soft"><Smartphone className="h-4 w-4" /><span className="text-xs uppercase tracking-[0.16em]">Install Ascendia Admin</span></div>
          <h3 className="mt-2 font-display text-lg font-semibold text-white">Enable notifications on iPhone</h3>
          <p className="mt-1 text-sm text-secondary">Apple requires the app to be added to your Home Screen before notifications can be turned on.</p>
          <ol className="mt-3 space-y-2 text-sm text-secondary">
            <li className="flex items-center gap-2"><Share className="h-4 w-4 text-gold-soft" /> Tap the <b className="text-white">Share</b> button in Safari</li>
            <li className="flex items-center gap-2"><PlusSquare className="h-4 w-4 text-gold-soft" /> Choose <b className="text-white">Add to Home Screen</b></li>
            <li className="flex items-center gap-2"><Bell className="h-4 w-4 text-gold-soft" /> Open <b className="text-white">Ascendia</b> from your Home Screen, then Enable notifications</li>
          </ol>
        </div>
      )}

      {/* Enable */}
      <div className="ascendia-card p-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="flex items-center gap-2 font-display text-lg font-semibold text-white"><Bell className="h-5 w-5 text-gold" /> Enable Phone Notifications</h3>
            <p className="mt-1 text-sm text-secondary">Get instant alerts on this device when customers submit quotes, rescues, bookings and more.</p>
            {!supported && iosNeedsInstall && <p className="mt-2 text-xs text-gold-soft">Add Ascendia to your Home Screen first (see steps above), then open it from there to enable notifications.</p>}
            {!supported && !iosNeedsInstall && <p className="mt-2 text-xs text-red-300">This browser/device does not support web push notifications.</p>}
          </div>
        </div>
        <div className="mt-4 flex flex-wrap gap-3">
          <button onClick={enable} disabled={!supported || busy || iosNeedsInstall} data-testid="enable-push-button" className="inline-flex items-center gap-2 rounded-xl bg-gold px-5 h-11 text-sm font-semibold text-black hover:bg-gold-soft disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
            <Bell className="h-4 w-4" /> {busy ? "Enabling…" : "Enable on this device"}
          </button>
          <button onClick={test} data-testid="send-test-button" className="inline-flex items-center gap-2 rounded-xl border border-[color:var(--border-strong)] px-5 h-11 text-sm text-white hover:bg-white/5 transition-colors">
            <Send className="h-4 w-4" /> Send Test Notification
          </button>
        </div>
      </div>

      {/* My devices */}
      <div className="ascendia-card p-6" data-testid="my-devices">
        <h3 className="flex items-center gap-2 font-display text-lg font-semibold text-white"><Smartphone className="h-5 w-5 text-gold-soft" /> My Devices</h3>
        <div className="mt-4 space-y-2">
          {devices.length === 0 && <p className="text-sm text-muted-c">No devices registered yet.</p>}
          {devices.map((d) => (
            <div key={d.id} data-testid="device-row" className="flex items-center justify-between gap-3 rounded-lg border border-[color:var(--border-subtle)] bg-black/30 p-3">
              <div className="min-w-0">
                <p className="text-sm font-medium text-white truncate">{d.label} {d.enabled ? <span className="text-emerald-400 text-xs">· Push enabled ✓</span> : <span className="text-white/40 text-xs">· Disabled</span>}</p>
                <p className="text-[11px] text-muted-c truncate">{(d.user_agent || "").slice(0, 60)}</p>
              </div>
              <div className="flex items-center gap-1.5">
                <button onClick={() => rename(d)} title="Rename" className="h-8 w-8 inline-flex items-center justify-center rounded-lg border border-[color:var(--border-subtle)] text-white/70 hover:bg-white/5"><Pencil className="h-3.5 w-3.5" /></button>
                <button onClick={() => toggle(d)} title={d.enabled ? "Disable" : "Enable"} className="h-8 w-8 inline-flex items-center justify-center rounded-lg border border-[color:var(--border-subtle)] text-white/70 hover:bg-white/5"><Power className="h-3.5 w-3.5" /></button>
                <button onClick={() => remove(d)} title="Remove" data-testid="remove-device" className="h-8 w-8 inline-flex items-center justify-center rounded-lg border border-red-500/30 text-red-300 hover:bg-red-500/10"><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Preferences */}
      {prefs && (
        <div className="ascendia-card p-6" data-testid="notification-prefs">
          <h3 className="flex items-center gap-2 font-display text-lg font-semibold text-white"><ShieldCheck className="h-5 w-5 text-gold-soft" /> Notification Preferences</h3>
          <div className="mt-4 inline-flex rounded-lg border border-[color:var(--border-subtle)] p-1">
            {[["all", "All notifications"], ["important", "Important only"]].map(([k, label]) => (
              <button key={k} onClick={() => setMode(k)} data-testid={`prefs-mode-${k}`} className={`rounded-md px-4 py-1.5 text-sm transition-colors ${prefs.mode === k ? "bg-gold text-black" : "text-white/70 hover:text-white"}`}>{label}</button>
            ))}
          </div>
          <div className="mt-4 grid sm:grid-cols-2 gap-2">
            {Object.keys(CATEGORY_LABELS).map((c) => (
              <label key={c} className="flex items-center justify-between gap-3 rounded-lg border border-[color:var(--border-subtle)] bg-black/20 px-3 py-2.5 cursor-pointer">
                <span className="text-sm text-secondary">{CATEGORY_LABELS[c]}</span>
                <input type="checkbox" checked={!!prefs.categories?.[c]} onChange={() => toggleCat(c)} data-testid={`prefs-cat-${c}`} className="h-4 w-4 accent-[#C8A14A]" />
              </label>
            ))}
          </div>
        </div>
      )}

      {/* Delivery log (owner only) */}
      {isOwner && (
        <div className="ascendia-card p-6" data-testid="delivery-log">
          <h3 className="flex items-center gap-2 font-display text-lg font-semibold text-white"><Activity className="h-5 w-5 text-gold-soft" /> Delivery Log <span className="text-xs text-muted-c font-normal">(owner diagnostics)</span></h3>
          <p className="mt-1 text-xs text-muted-c">Shows what the push service accepted. It does not claim a notification was physically seen.</p>
          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-sm min-w-[520px]">
              <thead><tr className="text-left text-muted-c border-b border-[color:var(--border-subtle)]"><th className="py-2 pr-3">Event</th><th className="px-3">When</th><th className="px-3">Targeted</th><th className="px-3">Accepted</th><th className="px-3">Failed</th></tr></thead>
              <tbody>
                {log.map((n) => (
                  <tr key={n.id} className="border-b border-[color:var(--border-subtle)]/40">
                    <td className="py-2 pr-3 text-white">{n.title}</td>
                    <td className="px-3 text-muted-c">{new Date(n.created_at).toLocaleString()}</td>
                    <td className="px-3 text-secondary">{n.delivery?.targeted ?? 0}</td>
                    <td className="px-3 text-emerald-400">{n.delivery?.sent ?? 0}</td>
                    <td className="px-3 text-red-300">{(n.delivery?.failed ?? 0) + (n.delivery?.removed ?? 0)}</td>
                  </tr>
                ))}
                {log.length === 0 && <tr><td colSpan={5} className="py-6 text-center text-muted-c">No events yet.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminNotifications;
